import { Card, CardContent } from "@/components/ui/card";
import { Activity, Target, Gauge, ArrowUpCircle, Wallet, Smartphone, Wifi, Tablet, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { fmtNum, fmtPct } from "@/lib/verizonSalesApi";

const pacingTone = (p) => (p == null ? "text-slate-500" : p >= 1 ? "text-emerald-600" : p >= 0.85 ? "text-amber-600" : "text-red-600");

const DELTA_KEY = { acts: "current_acts", quota: "current_quota", pacing: "pacing_pct", upgrades: "current_upgrades",
  topups: "current_topups", edge: "current_edge_acts", fwa: "current_fwa_acts", tablets: "current_tablets" };

export const Delta = ({ cur, prev, label, neutral, testId }) => {
  if (cur == null || prev == null || prev === 0) return null;
  const pct = (cur - prev) / Math.abs(prev);
  const flat = Math.abs(pct) < 0.0005;
  const Icon = flat ? Minus : pct > 0 ? TrendingUp : TrendingDown;
  const tone = flat || neutral ? "text-slate-500" : pct > 0 ? "text-emerald-600" : "text-red-600";
  return (
    <div className={`flex items-center gap-1 text-xs mt-1 font-medium ${tone}`} title={label} data-testid={testId}>
      <Icon className="w-3.5 h-3.5 shrink-0" /> {pct > 0 && !flat ? "+" : ""}{(pct * 100).toFixed(1)}% <span className="text-slate-400 font-normal truncate">{label}</span>
    </div>
  );
};

export const SalesKpis = ({ totals, period, compare }) => {
  const live = !period || period.is_current;
  const items = [
    { key: "acts", label: live ? "MTD Activations" : "Activations", value: fmtNum(totals.current_acts), sub: live ? `Prior month ${fmtNum(totals.prior_acts)}` : period.label, icon: Activity },
    { key: "quota", label: "Quota", value: fmtNum(totals.current_quota), sub: `${live ? "Pacing" : "Pacing (file)"} ${fmtNum(totals.pacing_acts)} acts${totals.goal_acts ? ` · Goal ${fmtNum(totals.goal_acts)}` : ""}`, icon: Target },
    { key: "pacing", label: "Pacing % to Quota", value: fmtPct(totals.pacing_pct), sub: `${totals.stores} stores`, icon: Gauge, tone: pacingTone(totals.pacing_pct) },
    { key: "upgrades", label: "Upgrades", value: fmtNum(totals.current_upgrades), icon: ArrowUpCircle },
    { key: "topups", label: "Top-Ups", value: fmtNum(totals.current_topups), icon: Wallet },
    { key: "edge", label: "Edge Acts", value: fmtNum(totals.current_edge_acts), icon: Smartphone },
    { key: "fwa", label: "Home Internet (FWA)", value: fmtNum(totals.current_fwa_acts), icon: Wifi },
    { key: "tablets", label: "Tablets", value: fmtNum(totals.current_tablets), icon: Tablet },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="vz-kpis">
      {items.map((k) => (
        <Card key={k.key} className="border-slate-200 hover:border-teal/50 transition-colors" data-testid={`vz-kpi-${k.key}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between text-xs uppercase tracking-wide text-slate-500">
              <span>{k.label}</span><k.icon className="w-4 h-4 text-teal" />
            </div>
            <div className={`text-2xl font-heading font-bold mt-1 ${k.tone || "text-navy"}`} data-testid={`vz-kpi-${k.key}-value`}>{k.value}</div>
            {k.sub && <div className="text-xs text-slate-500 mt-0.5">{k.sub}</div>}
            {compare && <Delta cur={totals[DELTA_KEY[k.key]]} prev={compare.totals?.[DELTA_KEY[k.key]]} label={compare.label} neutral={k.key === "quota"} testId={`vz-kpi-${k.key}-delta`} />}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
